import { useMemo, useState } from 'react';
import { PageLoader } from '@/components/ui/spinner';
import { useLocation } from 'wouter';
import { useQuery, useMutation } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { format, startOfMonth } from 'date-fns';
import { es } from 'date-fns/locale';
import type { DateRange } from 'react-day-picker';
import { toast } from 'sonner';
import {
  ImageOff,
  CircleCheck,
  Search,
  FileText,
  ExternalLink,
  Plus,
  Banknote,
  Copy,
  MoreVertical,
  ReceiptText,
} from 'lucide-react';
import { api, ApiCallError } from '@/lib/api';
import { AppShell } from '@/components/app-shell';
import { useTenantMe } from '@/hooks/use-tenant-me';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { DataTable } from '@/components/ui/data-table';
import { ImageLightbox } from '@/components/image-lightbox';
import { Empty, EmptyHeader, EmptyMedia, EmptyTitle, EmptyDescription } from '@/components/ui/empty';
import { ElectronicInvoiceFormDialog } from './electronic-invoice-form';
import { ElectronicInvoiceDetailDialog } from './electronic-invoice-detail';
import { InvoiceDetailDialog } from '@/components/invoice-detail-dialog';
import { ReceivablesTab } from './receivables-tab';

interface InvoiceRow {
  id: string;
  direction: 'purchase' | 'sale';
  tipoDocumento: string;
  invoiceNumber: string;
  issueDate: string;
  senderRazonSocial: string;
  senderRuc: string;
  grandTotal: string;
  currency: string;
  status: string;
  originalFileId: string | null;
  categoriaGasto: string | null;
  createdAt: string;
  // 'manual' = cargada por foto/OCR; 'set' = comprobante electrónico sincronizado
  // desde SET (sin foto → se muestra un icono en vez de la vista previa).
  source: 'manual' | 'set';
  // true (solo en filas manuales) si la foto coincide con un comprobante ya
  // traído de SET (mismo nº + RUC) → se marca "Duplicado" y se atenúa.
  duplicateOfSet?: boolean;
}

interface ElectronicInvoiceRow {
  id: string;
  invoiceNumber: string;
  clientName: string;
  clientRuc: string | null;
  grandTotal: string;
  currency: string;
  status: string;
  saleType: string;
  cdc: string | null;
  kudeUrl: string | null;
  isDemo: boolean;
  createdAt: string;
  // 'actuo' = factura emitida via Actuo; 'set' = venta ingestada del portal SET.
  // Las de 'set' no tienen KUDE ni cobro asociado (read-only).
  source?: 'actuo' | 'set';
}

const STATUS_LABEL: Record<string, string> = {
  pending_extraction: 'Procesando',
  pending_review: 'En revisión',
  approved: 'Aprobada',
  included_in_declaration: 'Incluida',
  rejected: 'Rechazada',
  set_synced: 'SET', // comprobante electrónico traído de SET (no pasa por revisión)
};

const E_STATUS_LABEL: Record<string, string> = {
  draft: 'Borrador',
  demo_approved: 'Emitida (demo)',
  issued: 'Emitida',
  sent: 'Enviada',
  void: 'Anulada',
  cancelled: 'Anulada',
  error: 'Error',
  set_synced: 'Recibida en SET', // venta ingestada del portal (no emitida por Actuo)
};

function statusVariant(s: string): 'success' | 'warning' | 'destructive' | 'secondary' {
  if (s === 'approved' || s === 'included_in_declaration' || s === 'set_synced') return 'success';
  if (s === 'rejected') return 'destructive';
  if (s === 'pending_review' || s === 'pending_extraction') return 'warning';
  return 'secondary';
}

function eStatusVariant(s: string): 'success' | 'warning' | 'destructive' | 'secondary' {
  if (s === 'demo_approved' || s === 'issued' || s === 'sent') return 'success';
  if (s === 'void' || s === 'cancelled' || s === 'error') return 'destructive';
  return 'secondary';
}

function tipoLabel(t: string): string {
  if (t === 'factura') return 'Factura';
  return t.replace('_', ' ').replace(/^\w/, (c) => c.toUpperCase());
}

function formatGs(amount: string, currency: string): string {
  const n = Number(amount);
  if (currency === 'PYG' || !currency) return `Gs ${Math.round(n).toLocaleString('es-PY')}`;
  return `${currency} ${n.toLocaleString('es-PY')}`;
}

export function AppInvoicesPage() {
  const [, navigate] = useLocation();
  const { data: me, isLoading: loadingMe } = useTenantMe();

  // Tab inicial controlado por `?tab=ingresos|egresos|cxc` (default egresos).
  // wouter no expone query string nativo — leemos directo de window.
  const initialTab =
    typeof window !== 'undefined'
      ? (() => {
          const t = new URLSearchParams(window.location.search).get('tab');
          if (t === 'ingresos') return 'electronicas';
          if (t === 'cxc') return 'cxc';
          return 'egresos';
        })()
      : 'egresos';

  // Un solo rango arriba: filtra Egresos e Ingresos server-side
  // (reduce carga). Cuentas por cobrar son saldos abiertos → ignora el rango.
  const [range, setRange] = useState<DateRange | undefined>({
    from: startOfMonth(new Date()),
    to: new Date(),
  });
  const from = range?.from ? format(range.from, 'yyyy-MM-dd') : '';
  const to = range?.to ? format(range.to, 'yyyy-MM-dd') : '';

  if (loadingMe) return <PageLoader />;
  if (!me) {
    navigate('/app/login');
    return null;
  }

  return (
    <AppShell>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Facturas</h1>
          <p className="text-sm text-muted-foreground">
            Tus comprobantes: los gastos (egresos) que cargás y las facturas que emitís (ingresos).
          </p>
        </div>
        <DateRangePicker
          value={range}
          onChange={setRange}
          placeholder="Rango de fechas"
          align="end"
          disabled={{ after: new Date() }}
        />
      </div>

      <Tabs defaultValue={initialTab}>
        <TabsList className="inline-flex w-auto">
          <TabsTrigger value="egresos">Egresos</TabsTrigger>
          <TabsTrigger value="electronicas">Ingresos</TabsTrigger>
          <TabsTrigger value="cxc">Cuentas por cobrar</TabsTrigger>
        </TabsList>
        <TabsContent value="egresos" className="mt-4">
          <EgresosTab from={from} to={to} />
        </TabsContent>
        <TabsContent value="electronicas" className="mt-4">
          <ElectronicasTab from={from} to={to} />
        </TabsContent>
        <TabsContent value="cxc" className="mt-4">
          <ReceivablesTab />
        </TabsContent>
      </Tabs>
    </AppShell>
  );
}

/* ───────────────────────── Egresos (gastos cargados) ───────────────────────── */

function EgresosTab({ from, to }: { from: string; to: string }) {
  const [viewerSrc, setViewerSrc] = useState<string | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);

  // El rango (del header de la página) filtra server-side; dentro del período
  // el DataTable hace búsqueda/orden/export client-side.
  const { data, isLoading } = useQuery({
    queryKey: ['app-invoices', from, to],
    queryFn: () => {
      const params = new URLSearchParams({ page: '1', pageSize: '1000' });
      if (from && to) {
        params.set('from', from);
        params.set('to', to);
      }
      return api<{ items: InvoiceRow[]; total: number }>(`/api/app/invoices?${params}`);
    },
  });

  const items = data?.items ?? [];

  const columns = useMemo<ColumnDef<InvoiceRow, unknown>[]>(
    () => [
      {
        id: 'img',
        header: '',
        enableSorting: false,
        cell: ({ row }) => {
          const inv = row.original;
          // Comprobante de SET: sin foto → icono distintivo (no vista previa).
          if (inv.source === 'set') {
            return (
              <div
                className="flex size-10 items-center justify-center rounded-md bg-success/10"
                title="Comprobante electrónico (SET)"
              >
                <ReceiptText className="size-4 text-success" />
              </div>
            );
          }
          return inv.originalFileId ? (
            <button
              type="button"
              onClick={(e) => {
                // Evita que el row-click abra el modal de detalle: la foto es
                // una acción explícita aparte (ver solo la imagen).
                e.stopPropagation();
                setViewerSrc(`/api/app/files/${inv.originalFileId}`);
              }}
              className="group relative flex size-10 items-center justify-center overflow-hidden rounded-md bg-muted ring-offset-background transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              title="Ver factura"
            >
              <img
                src={`/api/app/files/${inv.originalFileId}`}
                alt=""
                className="size-full object-cover transition group-hover:opacity-80"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                }}
              />
              <Search className="absolute size-4 text-white opacity-0 drop-shadow transition group-hover:opacity-100" />
            </button>
          ) : (
            <div className="flex size-10 items-center justify-center rounded-md bg-muted">
              <ImageOff className="size-4 text-muted-foreground" />
            </div>
          );
        },
      },
      {
        id: 'comprobante',
        header: 'Comprobante',
        accessorFn: (inv) => `${tipoLabel(inv.tipoDocumento)} ${inv.invoiceNumber ?? ''}`,
        cell: ({ row }) => (
          <div className={row.original.duplicateOfSet ? 'opacity-60' : ''}>
            <div className="flex items-center gap-1.5">
              <span className="font-medium leading-tight">{tipoLabel(row.original.tipoDocumento)}</span>
              {row.original.duplicateOfSet && (
                <Badge variant="warning" className="text-[10px]" title="Esta foto coincide con un comprobante ya traído de SET">
                  Duplicado
                </Badge>
              )}
            </div>
            <div className="font-mono text-xs text-muted-foreground">
              {row.original.invoiceNumber || '—'}
            </div>
          </div>
        ),
      },
      {
        id: 'emisor',
        header: 'Emisor',
        accessorFn: (inv) => `${inv.senderRazonSocial ?? ''} ${inv.senderRuc ?? ''}`,
        cell: ({ row }) => (
          <div>
            <div className="max-w-[16rem] truncate leading-tight">
              {row.original.senderRazonSocial || '—'}
            </div>
            <div className="font-mono text-xs text-muted-foreground">
              {row.original.senderRuc || '—'}
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'issueDate',
        header: 'Emisión',
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-sm">
            {format(new Date(row.original.issueDate + 'T12:00:00'), 'dd MMM yyyy', { locale: es })}
          </span>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Estado',
        cell: ({ row }) => (
          <span className="inline-flex items-center">
            <Badge variant={statusVariant(row.original.status)}>
              {STATUS_LABEL[row.original.status] ?? row.original.status}
            </Badge>
            {(row.original.status === 'approved' ||
              row.original.status === 'included_in_declaration') && (
              <span className="ml-2 inline-flex items-center gap-1 align-middle text-xs text-success">
                <CircleCheck className="size-3" />
              </span>
            )}
          </span>
        ),
      },
      {
        id: 'monto',
        header: 'Monto',
        accessorFn: (inv) => Number(inv.grandTotal),
        cell: ({ row }) => (
          <span className="font-semibold tabular-nums whitespace-nowrap">
            {formatGs(row.original.grandTotal, row.original.currency)}
          </span>
        ),
      },
    ],
    []
  );

  return (
    <div className="space-y-4">
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={items}
          pageSize={15}
          searchPlaceholder="Buscar por emisor, RUC o nº…"
          exportFileName="egresos"
          exportRows={(rows) =>
            rows.map((inv) => ({
              Comprobante: tipoLabel(inv.tipoDocumento),
              'Nº': inv.invoiceNumber || '',
              Emisor: inv.senderRazonSocial || '',
              RUC: inv.senderRuc || '',
              Emisión: format(new Date(inv.issueDate + 'T12:00:00'), 'dd/MM/yyyy'),
              Estado: STATUS_LABEL[inv.status] ?? inv.status,
              Monto: Number(inv.grandTotal),
              Moneda: inv.currency,
            }))
          }
          emptyMessage="Sin facturas en el período. Mandá la foto de tu factura por WhatsApp."
          onRowClick={(inv) => setDetailId(inv.id)}
        />
      )}

      <InvoiceDetailDialog
        id={detailId}
        onClose={() => setDetailId(null)}
        onOpenFile={(fileId) => setViewerSrc(`/api/app/files/${fileId}`)}
      />

      {/* Visor simple de la imagen de la factura (solo ver, sin edición) */}
      <Dialog open={!!viewerSrc} onOpenChange={(open) => !open && setViewerSrc(null)}>
        <DialogContent className="max-w-3xl gap-0 overflow-hidden p-0 sm:max-w-3xl">
          <DialogTitle className="border-b border-border px-4 py-3 text-base">Factura</DialogTitle>
          <div className="h-[78vh]">
            {viewerSrc && <ImageLightbox src={viewerSrc} alt="Factura" className="h-full" />}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ─────────────────── Facturas electrónicas (emitidas) ─────────────────── */

function ElectronicasTab({ from, to }: { from: string; to: string }) {
  const [formOpen, setFormOpen] = useState(false);
  const [viewId, setViewId] = useState<string | null>(null);
  // ID de detalle para filas SET (viven en otra tabla, modal distinto).
  const [setDetailId, setSetDetailId] = useState<string | null>(null);

  // El rango (header de la página) filtra server-side; el DataTable busca/exporta.
  const { data, isLoading } = useQuery({
    queryKey: ['app-electronic-invoices', from, to],
    queryFn: () => {
      const params = new URLSearchParams({ page: '1', pageSize: '1000' });
      if (from && to) {
        params.set('from', from);
        params.set('to', to);
      }
      return api<{ items: ElectronicInvoiceRow[]; total: number }>(
        `/api/app/electronic-invoices?${params}`
      );
    },
  });

  const items = data?.items ?? [];

  const columns = useMemo<ColumnDef<ElectronicInvoiceRow, unknown>[]>(
    () => [
      {
        id: 'comprobante',
        header: 'Comprobante',
        accessorFn: (inv) => inv.invoiceNumber ?? '',
        cell: ({ row }) => (
          <div>
            <div className="flex items-center gap-2 font-medium leading-tight">
              Factura
              {row.original.isDemo && (
                <Badge variant="secondary" className="align-middle text-[10px]">
                  demo
                </Badge>
              )}
              {row.original.source === 'set' && (
                <Badge variant="outline" className="align-middle text-[10px]" title="Venta ingestada del portal SET">
                  SET
                </Badge>
              )}
            </div>
            <div className="font-mono text-xs text-muted-foreground">
              {row.original.invoiceNumber || '—'}
            </div>
          </div>
        ),
      },
      {
        id: 'cliente',
        header: 'Cliente',
        accessorFn: (inv) => `${inv.clientName ?? ''} ${inv.clientRuc ?? ''}`,
        cell: ({ row }) => (
          <div>
            <div className="max-w-[16rem] truncate leading-tight">{row.original.clientName || '—'}</div>
            <div className="font-mono text-xs text-muted-foreground">{row.original.clientRuc || '—'}</div>
          </div>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Fecha',
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-sm">
            {format(new Date(row.original.createdAt), 'dd MMM yyyy', { locale: es })}
          </span>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Estado',
        cell: ({ row }) => (
          <Badge variant={eStatusVariant(row.original.status)}>
            {E_STATUS_LABEL[row.original.status] ?? row.original.status}
          </Badge>
        ),
      },
      {
        id: 'monto',
        header: 'Monto',
        accessorFn: (inv) => Number(inv.grandTotal),
        cell: ({ row }) => (
          <span className="font-semibold tabular-nums whitespace-nowrap">
            {formatGs(row.original.grandTotal, row.original.currency)}
          </span>
        ),
      },
      {
        id: 'acciones',
        header: '',
        enableSorting: false,
        cell: ({ row }) => <ElectronicInvoiceRowActions inv={row.original} />,
      },
    ],
    []
  );

  return (
    <div className="space-y-4">
      <ElectronicInvoiceFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={() => setFormOpen(false)}
      />

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <Card>
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <FileText />
              </EmptyMedia>
              <EmptyTitle>Sin facturas electrónicas todavía</EmptyTitle>
              <EmptyDescription>
                Las facturas que emitís a tus clientes (por el bot) aparecen acá.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
          <div className="flex justify-center pb-6">
            <Button onClick={() => setFormOpen(true)}>
              <Plus className="mr-2 size-4" /> Nueva factura electrónica
            </Button>
          </div>
        </Card>
      ) : (
        <DataTable
          columns={columns}
          data={items}
          pageSize={15}
          searchPlaceholder="Buscar por nº o cliente…"
          exportFileName="facturas-electronicas"
          exportRows={(rows) =>
            rows.map((inv) => ({
              'Nº': inv.invoiceNumber || '',
              Cliente: inv.clientName || '',
              RUC: inv.clientRuc ?? '',
              Fecha: format(new Date(inv.createdAt), 'dd/MM/yyyy'),
              Estado: E_STATUS_LABEL[inv.status] ?? inv.status,
              Condición: inv.saleType,
              Monto: Number(inv.grandTotal),
              Moneda: inv.currency,
            }))
          }
          toolbarActions={
            <Button size="sm" onClick={() => setFormOpen(true)}>
              <Plus /> Nueva
            </Button>
          }
          onRowClick={(inv) => {
            // Modal distinto según fuente: las de SET viven en `invoices`,
            // las de Actuo en `electronic_invoices`.
            if (inv.source === 'set') setSetDetailId(inv.id);
            else setViewId(inv.id);
          }}
        />
      )}

      <ElectronicInvoiceDetailDialog id={viewId} onClose={() => setViewId(null)} />
      <InvoiceDetailDialog id={setDetailId} onClose={() => setSetDetailId(null)} />
    </div>
  );
}

/* ─────────────────── Acciones de fila (factura electrónica) ─────────────────── */

function ElectronicInvoiceRowActions({ inv }: { inv: ElectronicInvoiceRow }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState<string | null>(null);

  const gen = useMutation({
    mutationFn: () =>
      api<{ paymentUrl: string }>(`/api/app/electronic-invoices/${inv.id}/payment-request`, {
        method: 'POST',
        body: JSON.stringify({ method: 'link' }),
      }),
    onSuccess: (r) => {
      setUrl(r.paymentUrl);
      setOpen(true);
    },
    onError: (e) =>
      toast.error(
        e instanceof ApiCallError ? (e.body.message ?? e.body.error) : 'No se pudo generar el cobro'
      ),
  });

  // Las ventas ingestadas de SET (source='set') son de otro emisor → no
  // se cobran ni tienen KUDE propio. Sin acciones → no mostramos el dropdown.
  const isSet = inv.source === 'set';
  const canCobrar = !isSet && inv.status !== 'void';
  const canKude = Boolean(inv.kudeUrl);
  if (!canCobrar && !canKude) return null;

  return (
    <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" title="Acciones">
            <MoreVertical className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {canCobrar && (
            <DropdownMenuItem disabled={gen.isPending} onClick={() => gen.mutate()}>
              <Banknote /> Generar cobro
            </DropdownMenuItem>
          )}
          {canKude && (
            <DropdownMenuItem asChild>
              <a href={inv.kudeUrl!} target="_blank" rel="noopener noreferrer">
                <ExternalLink /> Ver KUDE
              </a>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cobro generado</DialogTitle>
            <DialogDescription>
              Compartí este link con tu cliente para que pague la factura {inv.invoiceNumber} (modo demo).
            </DialogDescription>
          </DialogHeader>
          {url && (
            <div className="flex items-center gap-2">
              <Input readOnly value={url} />
              <Button
                variant="outline"
                size="icon"
                title="Copiar"
                onClick={() => {
                  void navigator.clipboard.writeText(url);
                  toast.success('Link copiado');
                }}
              >
                <Copy className="size-4" />
              </Button>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cerrar
            </Button>
            {url && (
              <Button asChild>
                <a href={url} target="_blank" rel="noopener noreferrer">
                  Abrir link
                </a>
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

